import type { TableAlign, TableColumn } from '@smartsash/ui';
import { flexRender } from '@tanstack/react-table';
import type { Column, Header, Row, RowData, TableMeta } from '@tanstack/react-table';
import type { ReactNode } from 'react';

declare module '@tanstack/react-table' {
  /**
   * Presentation metadata a `ColumnDef` carries for the `@smartsash/ui` `Table` (HS-869).
   *
   * The factory owns one column list — TanStack `ColumnDef`s (03-09 §2) — but the table it
   * renders through needs more than a header and a cell: which column titles the mobile card,
   * how wide the column is, and how to read a plain-text value out of a cell that returns JSX.
   * Without this seam an adopted screen silently loses its designed layout.
   */
  // The augmentation must repeat the original signature verbatim; `TValue` is unused here.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** The first column flagged `primary` becomes the mobile card's title. */
    primary?: boolean;
    /** CSS width for the column (e.g. `'12rem'`, `'20%'`). */
    width?: string;
    align?: TableAlign;
    /**
     * Plain text for search and sort when `cell` returns JSX. Falls back to
     * `row.getValue(id)`, which is empty for a display column — so a JSX cell that should be
     * searchable needs this.
     *
     * The table's `meta` is the second argument: a column whose text comes from a joined
     * lookup (an institution name resolved by id) has nowhere else to read it from, and
     * losing it would silently drop that column out of search, sort and the filter builder.
     */
    value?: (row: TData, meta: TableMeta<TData> | undefined) => string;
    /**
     * Report the column as sortable even though TanStack cannot: `getCanSort()` requires an
     * accessor, so a display column rendering JSX always answers `false`.
     */
    sortable?: boolean;
    /** Omit this column from the mobile card layout. */
    hideOnMobile?: boolean;
    /** Card-specific renderer (for example, omit desktop supporting text). */
    mobileCell?: (row: TData, meta: TableMeta<TData> | undefined) => ReactNode;
    /** Render this column as a compact badge below the mobile card title. */
    mobileVariant?: 'detail' | 'badge';
    /** Leading icon used when `mobileVariant` is `badge`. */
    mobileIcon?: ReactNode;
  }
}

/**
 * Map the table's leaf columns onto `ui` `TableColumn`s.
 *
 * Seeded from `table.getAllLeafColumns()` rather than from the first row's cells (HS-869): the
 * column list — and with it every `value` reader — must exist independently of the row set,
 * because client-scope search and sort narrow the rows *using* these columns, and a list derived
 * from `rows[0]` is empty exactly when there is nothing to show.
 */
export function tableColumnsFor<TRow>(
  columns: Column<TRow>[],
  headers: Header<TRow, unknown>[],
  tableMeta?: TableMeta<TRow>,
): TableColumn<Row<TRow>>[] {
  return columns.map((column) => {
    const meta = column.columnDef.meta;
    const header = headers.find((candidate) => candidate.column.id === column.id);
    const headerDefinition = column.columnDef.header;
    const readValue = meta?.value;
    const readMobileCell = meta?.mobileCell;

    return {
      id: column.id,
      header: header == null ? column.id : flexRender(headerDefinition, header.getContext()),
      cell: (row) => {
        const cell = row.getAllCells().find((candidate) => candidate.column.id === column.id);
        return cell == null ? null : flexRender(cell.column.columnDef.cell, cell.getContext());
      },
      value: (row) =>
        readValue == null
          ? String(row.getValue(column.id) ?? '')
          : readValue(row.original, tableMeta),
      sortable: meta?.sortable ?? column.getCanSort(),
      align: meta?.align,
      width: meta?.width,
      primary: meta?.primary,
      hideOnMobile: meta?.hideOnMobile,
      mobileVariant: meta?.mobileVariant,
      mobileIcon: meta?.mobileIcon,
      mobileCell:
        readMobileCell == null ? undefined : (row) => readMobileCell(row.original, tableMeta),
    };
  });
}
